import { useState, useEffect } from 'react';

import type { PageCardAppearance } from '@confluence/page-card/entry-points/cardTypes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	useLoggedInHomeViewsUserPreferences,
	useAnonymousHomeViewsUserPreferences,
} from './useHomeViewsUserPreferences.ts';
import type { PagesDisplayPersistenceOption } from './__generated__/useHomeViewsUserPreferencesQuery.graphql.ts';
import type { OptionsProps } from './Options.tsx';
import type { PillFilter } from './Pills.tsx';

type UseOptions = (pills?: PillFilter[], defaultPill?: string) => OptionsProps;

const AppearancePersistenceToToggle: {
	[key in PagesDisplayPersistenceOption]: PageCardAppearance;
} = {
	CARDS: 'grid',
	LIST: 'list',
	COMPACT_LIST: 'compact-list',
	// Relay adds "%future added value" to all enums for forward compatibility, but it will never actually be used
	'%future added value': 'grid',
};

const AppearanceToggleToPersistence: {
	[key in PageCardAppearance]: PagesDisplayPersistenceOption;
} = {
	grid: 'CARDS',
	list: 'LIST',
	'compact-list': 'COMPACT_LIST',
	'popup-view': 'CARDS',
	'end-of-page': 'CARDS',
	'compact-end-of-page': 'CARDS',
};

export const useOptions: UseOptions = (pills?, defaultPill?) => {
	const { isLoggedIn } = useSessionData();
	const [filterText, setFilterText] = useState('');
	const [pill, setPill] = useState(defaultPill);

	/**
	 * The default pill will only change if an override from query params changes.
	 * In this case, we need to update the state in order for the UI to reflect
	 * the new pill.
	 */
	useEffect(() => {
		setPill(defaultPill);
	}, [defaultPill]);

	const loggedInUserPreferences = useLoggedInHomeViewsUserPreferences();
	const anonymousUserPreferences = useAnonymousHomeViewsUserPreferences();

	const { loading, queryError, mutationError, appearance, updateHomeViewsUserPreference } =
		isLoggedIn ? loggedInUserPreferences : anonymousUserPreferences;

	const setAppearance = (appearance: PageCardAppearance) => {
		updateHomeViewsUserPreference(AppearanceToggleToPersistence[appearance]);
	};

	const optionsData: OptionsProps = {
		loading,
		queryError,
		mutationError,
		filterText,
		setFilterText,
		appearance: AppearancePersistenceToToggle[appearance],
		setAppearance,
	};

	if (pills) {
		optionsData.pill = pill;
		optionsData.pill = pill;
		optionsData.setPill = setPill;
		optionsData.pills = pills;
	}

	return optionsData;
};
